import React, { useCallback, useEffect, useMemo, useState } from "react";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import VStack from "../../ui_atoms/VStack";
import TextField from "../../components/TextField";
import ScreenCard from "../ScreenCard";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { useI18n } from "../../hooks/i18n-hooks";
import DateField from "../../components/DateField";
import { Gender } from "../../__generated__/graphql";
import { IconName } from "../../components/Icon";
import { checkPersonNameValidity, getAge } from "../../utils";
import DropdownField from "../../components/DropdownField";
import Gap from "../../ui_atoms/Gap";
import MarkdownView from "../../components/MarkdownView";
import { useIntercom } from "../../hooks/intercom";

type UserEditorScreenProps = StackScreenProps<RootStackParamList, "ClientProfileEdit">;

type EditorFields = {
    email: string;
    firstName: string;
    lastName: string;
    birthdate?: Date;
    gender?: Gender;
};

type ValidationErrors = Record<keyof EditorFields, string>;

const ClientProfileEditorScreen: React.FC<UserEditorScreenProps> = () => {
    const { user } = React.useContext(AuthManagerContext);
    const { presentMessageComposer } = useIntercom();

    const i18n = useI18n();
    const originalUser = useMemo<EditorFields>(
        () => ({
            email: user?.email || "",
            firstName: user?.firstName || "",
            lastName: user?.lastName || "",
            birthdate: user?.birthdate ? new Date(user.birthdate) : undefined,
            gender: user?.gender || undefined,
        }),
        [user],
    );

    const [editedUser, setEditedUser] = useState<EditorFields>(originalUser);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
        {} as ValidationErrors,
    );
    useEffect(() => {
        setEditedUser(originalUser);
    }, [originalUser]);

    const validate = useCallback(
        (values: EditorFields): boolean => {
            const result: Partial<ValidationErrors> = {};
            const firstNameError = checkPersonNameValidity(values.firstName);
            if (firstNameError) {
                result.firstName = i18n.t(firstNameError);
            }
            const lastNameError = checkPersonNameValidity(values.lastName);
            if (lastNameError) {
                result.lastName = i18n.t(lastNameError);
            }
            if (!values.birthdate) {
                result.birthdate = i18n.t("user.errors.birthdate");
            } else if (getAge(values.birthdate) < 18) {
                result.birthdate = i18n.t("user.errors.birthdate_18yo");
            }
            setValidationErrors(result as ValidationErrors);
            return Object.keys(result).length == 0;
        },
        [i18n],
    );

    useEffect(() => {
        validate(editedUser);
    }, [validate, editedUser]);

    const genderData = useMemo(
        () => [
            {
                label: i18n.t("civility.male"),
                value: Gender.Male,
                icon: "gender-male" as IconName,
            },
            {
                label: i18n.t("civility.female"),
                value: Gender.Female,
                icon: "gender-female" as IconName,
            },
        ],
        [i18n],
    );

    const onValidate = useCallback(async () => {
        await presentMessageComposer();
    }, [presentMessageComposer]);

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("profile.contact_support"),
                    onPress: onValidate,
                    mode: "secondary",
                },
            ]}
        >
            <VStack v={1} gap={1} alignment="stretch" h="2/3">
                <TextField
                    error={validationErrors.firstName}
                    value={editedUser.firstName}
                    label={i18n.t("user.first_name")}
                    disabled
                />
                <TextField
                    error={validationErrors.lastName}
                    value={editedUser.lastName}
                    label={i18n.t("user.last_name")}
                    autoComplete="family-name"
                    disabled
                />

                <DateField
                    error={validationErrors.birthdate}
                    value={editedUser.birthdate}
                    label={i18n.t("user.birthdate")}
                    disabled
                    display="spinner"
                />
                <DropdownField
                    items={genderData}
                    error={validationErrors.gender}
                    onSelect={() => {}}
                    value={editedUser.gender}
                    label={i18n.t("user.gender")}
                    disabled
                />
                <Gap gap={0.5} />
                <MarkdownView
                    markdown={i18n.t("profile.contact_us_for_change")}
                    onLinkPress={() => {
                        void presentMessageComposer();
                        return false;
                    }}
                />
            </VStack>
        </ScreenCard>
    );
};

export default ClientProfileEditorScreen;
